import * as React from 'react';
import type { PresenceMotionSlotProps } from '@fluentui/react-motion';
import { PositioningVirtualElement, SetVirtualMouseTarget } from '@fluentui/react-positioning';
import type { PositioningShorthand } from '@fluentui/react-positioning';
import type { PortalProps } from '@fluentui/react-portal';
import type { ComponentProps, ComponentState, JSXElement, Slot } from '@fluentui/react-utilities';
import type { MenuContextValue } from '../../contexts/menuContext';
import type { MenuListProps } from '../MenuList/MenuList.types';

export type MenuSlots = {
  /**
   * Slot for the surface motion animation.
   * For more information refer to the [Motion docs page](https://react.fluentui.dev/?path=/docs/motion-motion-slot--docs).
   */
  surfaceMotion: Slot<PresenceMotionSlotProps>;
};

export type InternalMenuSlots = {
  surfaceMotion: NonNullable<Slot<PresenceMotionSlotProps>>;
};

/**
 * Extends and drills down Menulist props to simplify API
 */
export type MenuProps = ComponentProps<Partial<MenuSlots>> &
  Pick<PortalProps, 'mountNode'> &
  Pick<
    MenuListProps,
    'checkedValues' | 'defaultCheckedValues' | 'hasCheckmarks' | 'hasIcons' | 'onCheckedValueChange'
  > & {
    /**
     * Can contain two children including `MenuTrigger` and `MenuPopover`.
     * Alternatively can only contain `MenuPopover` if using a custom `target`.
     */
    children: [JSXElement, JSXElement] | JSXElement;

    /**
     * Sets the delay for mouse open/close for the popover one mouse enter/leave
     */
    hoverDelay?: number;

    /**
     * Root menus are rendered out of DOM order on `document.body`, use this to render the menu in DOM order
     * This option is disregarded for submenus
     *
     * @default false
     */
    inline?: boolean;

    /**
     * Call back when the component requests to change value
     * The `open` value is used as a hint when directly controlling the component
     */
    // eslint-disable-next-line @nx/workspace-consistent-callback-type -- can't change type of existing callback
    onOpenChange?: (e: MenuOpenEvent, data: MenuOpenChangeData) => void;

    /**
     * Whether the popup is open
     *
     * @default false
     */
    open?: boolean;

    /**
     * Whether the popup is open by default
     *
     * @default false
     */
    defaultOpen?: boolean;

    /**
     * Opens the menu on right click (context menu), removes all other menu open interactions
     *
     * @default false
     */
    openOnContext?: boolean;

    /**
     * Opens the menu on hover
     *
     * @default false
     */
    openOnHover?: boolean;

    /**
     * Do not dismiss the menu when a menu item is clicked
     *
     * @default false
     */
    persistOnItemClick?: boolean;

    /**
     * Configures the positioned menu
     */
    positioning?: PositioningShorthand;

    /**
     * Close when scroll outside of it
     *
     * @default false
     */
    closeOnScroll?: boolean;
  };

export type MenuState = ComponentState<InternalMenuSlots> &
  Required<
    Pick<
      MenuProps,
      | 'hasCheckmarks'
      | 'hasIcons'
      | 'mountNode'
      | 'inline'
      | 'checkedValues'
      | 'onCheckedValueChange'
      | 'open'
      | 'openOnHover'
      | 'closeOnScroll'
      | 'hoverDelay'
      | 'openOnContext'
      | 'persistOnItemClick'
    >
  > & {
    /**
     * Anchors the popper to the mouse click for context events
     */
    contextTarget?: PositioningVirtualElement;

    /**
     * Whether this menu is a submenu
     */
    isSubmenu: boolean;

    /**
     * Internal react node that just simplifies handling children
     */
    menuPopover: React.ReactNode;

    /**
     * The ref for the popup
     */
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    menuPopoverRef: React.MutableRefObject<HTMLElement | null>;

    /**
     * Internal react node that just simplifies handling children
     */
    menuTrigger: React.ReactNode;

    /**
     * A callback to set the target of the popper to the mouse click for context events
     */
    setContextTarget: SetVirtualMouseTarget;

    /**
     * Callback to open/close the popup
     */
    setOpen: (e: MenuOpenEvent, data: MenuOpenChangeData & { ignoreHoverDelay?: boolean }) => void;

    /**
     * Id for the MenuTrigger element for aria relationship
     */
    triggerId: string;

    /**
     * The ref for the MenuTrigger, used for popup positioning
     */
    // eslint-disable-next-line @typescript-eslint/no-deprecated
    triggerRef: React.MutableRefObject<HTMLElement | null>;

    /**
     * Call back when the component requests to change value
     * The `open` value is used as a hint when directly controlling the component
     * @deprecated this property is not used internally anymore,
     * the signature remains just to avoid breaking changes
     */
    onOpenChange?: (e: MenuOpenEvent, data: MenuOpenChangeData) => void;
    /**
     * Default values to be checked on mount
     @deprecated this property is not used internally anymore,
     * the signature remains just to avoid breaking changes
     */
    defaultCheckedValues?: Record<string, string[]>;

    /**
     * An optional safe zone area to be rendered around the menu
     */
    safeZone?: React.ReactElement | null;
  };

export type MenuContextValues = {
  menu: MenuContextValue;
};

/**
 * The supported events that will trigger open/close of the menu
 */
export type MenuOpenEvent = MenuOpenChangeData['event'];
/**
 * @deprecated use MenuOpenEvent instead
 */
export type MenuOpenEvents = MenuOpenEvent;

/**
 * Data attached to open/close events
 */
export type MenuOpenChangeData = {
  /**
   * indicates whether the request for the open state was bubbled from a nested menu
   */
  bubble?: boolean;
  /**
   * Indicates whether the change of state was a keyboard interaction
   * @deprecated
   * This should not be used, since `Enter`, `Space` and click should be interpreted as the same thing as a click
   */
  keyboard?: boolean;
  open: boolean;
} & (
  | {
      type: 'menuTriggerContextMenu';
      event: React.MouseEvent<HTMLElement>;
    }
  | {
      type: 'menuTriggerClick';
      event: React.MouseEvent<HTMLElement>;
    }
  | {
      type: 'menuTriggerMouseEnter';
      event: React.MouseEvent<HTMLElement>;
    }
  | {
      type: 'menuSafeZoneMouseEnter';
      event: React.MouseEvent;
    }
  | {
      type: 'menuSafeZoneTimeout';
      event: Event;
    }
  | {
      type: 'menuTriggerMouseLeave';
      event: React.MouseEvent<HTMLElement>;
    }
  | {
      type: 'menuTriggerMouseMove';
      event: React.MouseEvent<HTMLElement>;
    }
  | {
      type: 'menuTriggerKeyDown';
      event: React.KeyboardEvent<HTMLElement>;
    }
  | {
      type: 'menuItemClick';
      event: React.MouseEvent<HTMLElement>;
    }
  | {
      type: 'menuPopoverMouseEnter';
      event: React.MouseEvent<HTMLElement>;
    }
  | {
      type: 'menuPopoverKeyDown';
      event: React.KeyboardEvent<HTMLElement>;
    }
  | {
      type: 'clickOutside';
      event: MouseEvent | TouchEvent;
    }
  | {
      type: 'scrollOutside';
      event: MouseEvent | TouchEvent;
    }
  | {
      type: 'menuMouseEnter';
      event: MouseEvent | TouchEvent;
    }
);
